import { useState, useEffect, useMemo } from 'react';
import { useGameState } from '../GameProvider';
import { useTimer } from '../hooks/useTimer';
import gameService from '../gameService';
import './DealerSuccession.css';

export default function DealerSuccession() {
  const state = useGameState();
  const { gameId, uid, players, dealerUid, dealerSuccession } = state;

  const [graceStart, setGraceStart] = useState(null);
  const [votingStart, setVotingStart] = useState(null);
  const [cooldownUntil, setCooldownUntil] = useState(null);

  // Read dealerConnected from meta (synced via SYNC_META in useFirebaseSync)
  // Need to add dealerConnected to the reducer state shape — add it in gameReducer SYNC_META
  const dealerConnected = state.dealerConnected !== false; // defaults true if undefined

  const isDealer = uid === dealerUid;

  // Grace period timer (30s)
  const { remaining: graceRemaining, isExpired: graceExpired } = useTimer(
    graceStart, 30, null
  );

  // Voting timeout (60s after grace)
  const { isExpired: votingExpired } = useTimer(
    votingStart, 60, null
  );

  // Start grace period when dealer disconnects — debounce to avoid false alarms
  const [disconnectTimer, setDisconnectTimer] = useState(null);
  useEffect(() => {
    if (!dealerConnected && !isDealer && !graceStart) {
      // Wait 5s before showing disconnect UI (avoid momentary blips)
      const timer = setTimeout(() => {
        setGraceStart(Date.now());
      }, 5000);
      setDisconnectTimer(timer);
    }
    if (dealerConnected) {
      // Dealer reconnected — cancel everything
      if (disconnectTimer) clearTimeout(disconnectTimer);
      setDisconnectTimer(null);
      if (graceStart) {
        setGraceStart(null);
        setVotingStart(null);
      }
    }
    return () => { if (disconnectTimer) clearTimeout(disconnectTimer); };
  }, [dealerConnected, isDealer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start voting after grace expires
  useEffect(() => {
    if (graceExpired && !votingStart) {
      setVotingStart(Date.now());
    }
  }, [graceExpired, votingStart]);

  const candidate = dealerSuccession?.candidate;
  const votes = useMemo(() => dealerSuccession?.votes || {}, [dealerSuccession?.votes]);

  const activeUids = useMemo(() =>
    Object.entries(players)
      .filter(([, p]) => !p.kicked && !p.sittingOut)
      .filter(([id]) => id !== dealerUid)
      .map(([id]) => id),
    [players, dealerUid]
  );

  // Auto-promote on voting timeout
  useEffect(() => {
    if (!votingExpired || isDealer) return;
    // Auto-promote by seat index order
    const sorted = [...activeUids].sort();
    if (sorted[0] === uid) {
      gameService.promoteDealer(gameId, uid);
    }
  }, [votingExpired, activeUids, uid, gameId, isDealer]);

  // Check votes for unanimity
  useEffect(() => {
    if (!candidate) return;
    const voterUids = activeUids.filter(id => id !== candidate.uid);
    const allVoted = voterUids.every(id => votes[id] !== undefined);
    if (!allVoted) return;

    const allApproved = voterUids.every(id => votes[id] === true);
    if (allApproved) {
      gameService.promoteDealer(gameId, candidate.uid);
    } else {
      // Rejected — set cooldown for candidate
      if (candidate.uid === uid) {
        setCooldownUntil(Date.now() + 3000);
      }
    }
  }, [candidate, votes, activeUids, gameId, uid]);

  if (isDealer || !graceStart) return null;

  const inCooldown = cooldownUntil && Date.now() < cooldownUntil;

  // Grace period
  if (!graceExpired) {
    return (
      <div className="dealer-succession">
        <p>Dealer disconnected. Waiting for reconnect... ({graceRemaining}s)</p>
      </div>
    );
  }

  // Voting in progress
  if (candidate) {
    if (candidate.uid === uid) {
      return (
        <div className="dealer-succession">
          <p>Waiting for approval from other players...</p>
        </div>
      );
    }

    if (votes[uid] === undefined) {
      return (
        <div className="dealer-succession">
          <p><strong>{candidate.displayName}</strong> wants to become dealer</p>
          <div className="dealer-succession__actions">
            <button className="btn btn--primary" onClick={() => gameService.voteDealer(gameId, uid, true)}>
              Approve
            </button>
            <button className="btn btn--secondary" onClick={() => gameService.voteDealer(gameId, uid, false)}>
              Reject
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="dealer-succession">
        <p>Waiting for other votes...</p>
      </div>
    );
  }

  // Become Dealer button
  return (
    <div className="dealer-succession">
      <p>Dealer disconnected</p>
      <button
        className="btn btn--primary"
        disabled={inCooldown}
        onClick={() => gameService.requestDealer(gameId, uid, players[uid]?.displayName || 'Player')}
      >
        {inCooldown ? 'Wait...' : 'Become Dealer'}
      </button>
    </div>
  );
}
